import React from "react";
import styled from "styled-components";
import { FeatureRequest } from "interfaces/feature-request";
import { EmptyStateHeader, Paragraph, TabHeader } from "../shared/generic";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";
import moment from "moment";
import styles from "styles/styles";

const CommentCreator = styled.p`
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: ${styles.colors.grey7};
`;

const CommentDate = styled.p`
    margin-top: 0;
    line-height: 12px;
    font-size: 14px;
    font-weight: 400;
    color: ${styles.colors.grey7};
`;

const Comment = styled.div<{ indent?: boolean }>`
    background-color: ${styles.colors.lightGrey};
    margin-bottom: 1.5rem;
    padding: 0.5rem 1rem;
    ${(props) => props.indent && "margin-left: 2rem;"}
`;

interface CommentCardProps {
    creatorName: string;
    createdAt: string;
    body: string;
    indent?: boolean;
}

const renderHtml = (html: string) => {
    const paragraphElements = new DOMParser()
        .parseFromString(html, "text/html")
        .querySelectorAll("p");

    return Array.from(paragraphElements)
        .map((p) => Array.from(p.childNodes).map((node) => node.textContent?.trim()))
        .flat()
        .filter(Boolean)
        .map((textContent, idx) => <Paragraph key={idx}>{textContent}</Paragraph>);
};

const CommentCard: React.FC<CommentCardProps> = ({ creatorName, createdAt, body, indent }) => (
    <Comment indent={indent}>
        <CommentCreator>{creatorName}</CommentCreator>
        <CommentDate>{moment(createdAt).format("DD.MM.YYYY")}</CommentDate>
        {renderHtml(body)}
    </Comment>
);

const CommentsTab: React.FC<{
    featureRequest: FeatureRequest;
}> = ({ featureRequest }) => {
    const translate = useTranslate();

    return (
        <>
            <TabHeader>{translate("featureRequests.featureRequestComments.title")}</TabHeader>
            <div>
                {featureRequest.updates.length === 0 && (
                    <EmptyStateHeader>
                        {translate("featureRequests.featureRequestComments.missingDescription")}
                    </EmptyStateHeader>
                )}

                {featureRequest.updates.map((update) => (
                    <React.Fragment key={update.id}>
                        <CommentCard
                            key={update.id}
                            creatorName={update.creator.name}
                            createdAt={update.created_at}
                            body={update.body}
                        />

                        {update.replies.map((reply) => (
                            <CommentCard
                                key={reply.id}
                                creatorName={reply.creator.name}
                                createdAt={reply.created_at}
                                body={reply.body}
                                indent
                            />
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </>
    );
};

export default CommentsTab;
